import type { ZodSchema } from 'zod'
import type { Result } from '@ekorank/shared'

// In production (CF Pages), set VITE_API_BASE_URL to the API worker URL.
// In local dev, leave it unset — the Vite proxy forwards /api → :8787.
export const API_BASE = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? ''

/**
 * Typed fetch wrapper.
 * Every response is validated against a Zod schema from @ekorank/shared.
 * Returns a Result<T> — never throws.
 */
export async function apiFetch<T>(
  path: string,
  schema: ZodSchema<T>,
  options: RequestInit = {},
): Promise<Result<T>> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> | undefined),
      },
    })
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Network error' }
  }

  let data: unknown
  try {
    data = await res.json()
  } catch {
    return { ok: false, error: `Failed to parse response (status ${res.status})` }
  }

  if (!res.ok) {
    const errData = data as Record<string, unknown>
    const raw = errData['error']
    let msg: string
    if (typeof raw === 'string') {
      msg = raw
    } else if (raw && typeof raw === 'object') {
      // Zod fieldErrors: { fieldName: ["msg1", ...], ... }
      const parts = Object.entries(raw as Record<string, unknown>)
        .flatMap(([field, errs]) =>
          Array.isArray(errs) ? errs.map((e) => `${field}: ${e}`) : [`${field}: invalid`],
        )
      msg = parts.join('; ') || `HTTP ${res.status}`
    } else {
      msg = `HTTP ${res.status}`
    }
    return { ok: false, error: msg }
  }

  const parsed = schema.safeParse(data)
  if (!parsed.success) {
    return { ok: false, error: `Response validation failed: ${parsed.error.message}` }
  }

  return { ok: true, data: parsed.data }
}

/** Helper for POST requests with a JSON body */
export function apiPost<T>(
  path: string,
  schema: ZodSchema<T>,
  body: unknown,
): Promise<Result<T>> {
  return apiFetch(path, schema, { method: 'POST', body: JSON.stringify(body) })
}

/** Helper for PATCH requests with a JSON body */
export function apiPatch<T>(
  path: string,
  schema: ZodSchema<T>,
  body: unknown,
): Promise<Result<T>> {
  return apiFetch(path, schema, { method: 'PATCH', body: JSON.stringify(body) })
}

/** Helper for PUT requests with a JSON body */
export function apiPut<T>(
  path: string,
  schema: ZodSchema<T>,
  body: unknown,
): Promise<Result<T>> {
  return apiFetch(path, schema, { method: 'PUT', body: JSON.stringify(body) })
}

/** Helper for DELETE requests */
export function apiDelete<T>(
  path: string,
  schema: ZodSchema<T>,
): Promise<Result<T>> {
  return apiFetch(path, schema, { method: 'DELETE' })
}
